import { BackendValidationIssue } from '../apiClient/apiClient';

type ValidationIssuesPayload = {
  errors?: unknown;
  warnings?: unknown;
};

type ValidationErrorResponseData = {
  validationIssues?: ValidationIssuesPayload;
  validationErrors?: unknown;
  message?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getResponseData = (
  error: unknown
): ValidationErrorResponseData | null => {
  if (error === null || error === undefined) {
    return null;
  }

  if ('validationIssues' in (error as any)) {
    return error as ValidationErrorResponseData;
  }

  if (!isRecord(error) || !('response' in error)) {
    return null;
  }

  const response = error.response;
  if (!isRecord(response) || !('data' in response)) {
    return null;
  }

  const data = response.data;
  if (!isRecord(data)) {
    return null;
  }

  return data as ValidationErrorResponseData;
};

const getStatusCode = (error: unknown): number | undefined => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return undefined;
  }

  return typeof error.response.status === 'number'
    ? error.response.status
    : undefined;
};

const toBackendValidationIssue = (
  value: unknown,
  type: 'error' | 'warning'
): BackendValidationIssue | null => {
  if (!isRecord(value) || typeof value.message !== 'string') {
    return null;
  }

  return {
    message: value.message,
    affectedFields: Array.isArray(value.affectedFields)
      ? value.affectedFields.filter(
          (field): field is string => typeof field === 'string'
        )
      : [],
    feature: typeof value.feature === 'string' ? value.feature : '',
    code: isRecord(value.code) ? value.code : {},
    severity: type,
  };
};

const toValidationIssues = (
  value: unknown,
  type: 'error' | 'warning'
): BackendValidationIssue[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => toBackendValidationIssue(item, type))
    .filter((item): item is BackendValidationIssue => item !== null);
};

export const getValidationIssueErrors = (
  error: unknown
): BackendValidationIssue[] => {
  const data = getResponseData(error);

  if (!data) {
    return [];
  }

  const issueErrors = toValidationIssues(
    data.validationIssues?.errors,
    'error'
  );
  if (issueErrors.length > 0) {
    return issueErrors;
  }

  const errors = toValidationIssues(data.validationErrors, 'error');
  if (errors.length > 0) {
    return errors;
  }

  if (
    getStatusCode(error) === 400 &&
    typeof data.message === 'string' &&
    data.message.trim().length > 0
  ) {
    return [
      {
        message: data.message,
        affectedFields: [],
        feature: '',
        code: {},
        severity: 'error',
      },
    ];
  }

  return [];
};

export const getValidationIssueWarnings = (
  error: unknown
): BackendValidationIssue[] => {
  const data = getResponseData(error);

  if (!data) {
    return [];
  }

  const issueWarnings = toValidationIssues(
    data.validationIssues?.warnings,
    'warning'
  );
  if (issueWarnings.length > 0) {
    return issueWarnings;
  }

  return [];
};

export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage: string
): string => {
  const data = getResponseData(error);

  if (typeof data?.message === 'string' && data.message.trim().length > 0) {
    return data.message;
  }

  if (isRecord(error) && typeof error.message === 'string') {
    return error.message;
  }

  return fallbackMessage;
};
